'use client';
/**
 * EXPRESSO — LA VEILLE BIOLOGIE DU JOUR, UNE INFORMATION ET PAS PLUS.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Demande de la RQ du 05/10/2026 : « un nouvel encart qui serait une sorte de
 * veille concernant la biologie médicale de routine. Cibler les informations
 * de la Tunisie est un plus. L'idée est d'afficher une info intéressante par
 * jour. Pas plus. Résumé et lien de l'info simplement. »
 *
 * ── UNE SEULE, ET C'EST LA CONTRAINTE PRINCIPALE ──────────────────────────
 *
 * « Pas plus » n'est pas une limite d'affichage, c'est l'idée même. Une liste
 * de cinq articles au tableau de bord ne se lit pas : elle se repousse à plus
 * tard, puis se referme. Une seule information, courte, avec son lien, se lit
 * le temps d'un café — d'où le nom.
 *
 * Le choix est fait PAR LE SERVEUR et ENREGISTRÉ pour la journée
 * (`backend/src/expresso-choix.ts`, module pur éprouvé par `npm test`) : tout
 * le monde voit la même, et elle ne change pas entre le matin et
 * l'après-midi. Un encart qui se renouvellerait à chaque rafraîchissement
 * empêcherait d'en parler à deux.
 *
 * ── ⚠️ CE QUE CET ENCART NE DOIT JAMAIS FAIRE ─────────────────────────────
 *
 *  · AFFIRMER SANS LIEN. Le lien est obligatoire côté serveur, et il est
 *    toujours rendu ici : on doit pouvoir aller lire l'original. Une veille
 *    sans source est une rumeur ;
 *  · FAIRE PASSER LE RÉSUMÉ DE L'ASSISTANT POUR LA SOURCE. Il est annoncé en
 *    toutes lettres (« résumé par l'assistant »). Sur le tableau de bord d'un
 *    laboratoire accrédité, un texte sans provenance se cite ;
 *  · SE TAIRE QUAND IL EST VIDE. « Rien de neuf aujourd'hui » et « le service
 *    de références est injoignable » ne veulent pas dire la même chose, et le
 *    serveur rend lequel des deux (`motif`). Un cadre vide sans phrase, c'est
 *    le piège des faux vides ;
 *  · ⚠️ NI DEVENIR UNE OBLIGATION. Aucun accusé de lecture, aucun compteur,
 *    aucune relance : personne ne doit rendre compte d'avoir lu l'expresso.
 *    C'est un agrément. Le jour où il se mesure, il se subit.
 */
import React from 'react';
import { I, MarqueIA, card } from '@/components/ui';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';

interface Info {
  jour: string;
  titre: string;
  resume: string;
  revue: string;
  auteurs: string;
  datePublication: string;
  lien: string;
}
type Reponse = { ok?: boolean; expresso?: Info | null; motif?: string };

/** Les deux vides, dits chacun avec ses mots. ⚠️ Ne jamais les fondre en « rien
 *  à afficher » : le premier parle de la littérature, le second d'une panne —
 *  et seul le second mérite qu'on prévienne l'informatique. */
const PHRASE_VIDE: Record<string, string> = {
  'tout-lu': 'Rien de neuf aujourd’hui : tout ce qui a paru ce mois-ci est déjà passé ici.',
  injoignable: 'Le service de références bibliographiques ne répond pas en ce moment. L’encart reprendra de lui-même.',
};

/** Les trois premiers auteurs, puis « et al. ». Une liste de vingt-deux noms
 *  occuperait plus de place que le résumé qu'elle accompagne. */
function auteursCourts(t: string): string {
  const noms = String(t || '').split(',').map((x) => x.trim()).filter(Boolean);
  if (!noms.length) return '';
  return noms.length <= 3 ? noms.join(', ') : `${noms.slice(0, 3).join(', ')} et al.`;
}

/** La date en clair, et RIEN si elle est illisible — jamais « 1 janv. 1970 ».
 *  ⚠️ `T00:00:00` et non la chaîne nue : `new Date('2026-10-02')` est lu en
 *  UTC, et un fuseau à l'ouest ramènerait l'article au 1er octobre. */
function dateCourte(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return '';
  return fmtDate(iso);
}

export function Expresso() {
  const { session } = useGed();
  /* `null` = on ne sait pas encore. ⚠️ Distinct de `{expresso: null}`, qui est
     un fait du jour. Les confondre ferait clignoter « rien de neuf » le temps
     du chargement — une affirmation, avant même d'avoir demandé. */
  const [rep, setRep] = React.useState<Reponse | null>(null);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/expresso', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      /* Un refus ou une coupure ne montre RIEN plutôt qu'un message d'erreur :
         l'encart est un agrément, pas une fonction. Le faire crier ferait
         passer un service tiers lent pour une panne du laboratoire. */
      .then((d: Reponse | null) => { if (vivant && d?.ok) setRep(d); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [session.key]);

  if (!rep) return null;
  const info = rep.expresso || null;
  const phrase = !info ? (PHRASE_VIDE[String(rep.motif || '')] || '') : '';
  /* Rien à dire du tout (motif inconnu) : on ne laisse pas un cadre vide au
     tableau de bord. Un encart muet se lit comme une panne. */
  if (!info && !phrase) return null;

  return (
    <section style={{ ...card, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 16px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
        <MarqueIA s={14} />
        <h2 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>Expresso</h2>
        {/* Le sous-titre porte le DOMAINE, que le nom ne dit pas. Il est au
            singulier (« la veille du jour ») parce que c'est la promesse :
            une information, et pas une liste. */}
        {/* ⚠️ `flex: 1` MAINTENU bien qu'il n'y ait plus rien à sa droite : il
            pousse le sous-titre contre le titre plutôt que de l'espacer, et le
            jour où une étiquette revient à droite, elle se place toute seule.
            (Une étiquette « TUNISIE » y vivait jusqu'au 06/10/2026.) */}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>la veille biologie du jour</span>
      </div>

      {!info ? (
        <p style={{ margin: 0, padding: '14px 16px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {phrase}
        </p>
      ) : (
        <div style={{ padding: '12px 16px', display: 'grid', gap: 8 }}>
          {/* Le titre reste dans sa langue d'origine, et ce n'est pas un
              oubli : c'est sous ce titre qu'on retrouvera l'article en le
              cherchant. Le traduire aurait rendu la recherche impossible. */}
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)', lineHeight: 1.4 }}>
            {info.titre}
          </p>

          {info.resume ? (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>
              {info.resume}
            </p>
          ) : (
            /* ⚠️ PAS DE RÉSUMÉ ≠ PAS D'INFORMATION. L'absence se DIT, parce
               qu'un titre seul au-dessus d'un lien se lit comme un encart à
               moitié chargé. */
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, fontStyle: 'italic' }}>
              Pas de résumé en français pour cette référence — le titre et le lien ci-dessous mènent à l’original.
            </p>
          )}

          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {[info.revue, auteursCourts(info.auteurs), dateCourte(info.datePublication)].filter(Boolean).join(' · ')}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* `rel="noreferrer"` autant que `noopener` : le second protège
                l'onglet, le premier évite d'annoncer à l'éditeur depuis quelle
                adresse interne on arrive. */}
            <a href={info.lien} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>
              Lire l’article <I n="lien-externe" s={12} />
            </a>
            <span style={{ flex: 1 }} />
            {info.resume && (
              /* La provenance du résumé, ÉCRITE. Discrète, mais présente :
                 sans elle, deux phrases de français simple sur un tableau de
                 bord de laboratoire se citent comme une source. */
              <span title="Résumé produit automatiquement à partir du résumé de l’éditeur. Il n’a pas été relu : en cas de doute, l’article fait foi."
                style={{ fontSize: 11, color: 'var(--text-faint)' }}>
                résumé par l’assistant, non relu
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
